/**
 * AYU Gamification — Vollstaendige Uebersicht
 * Abzeichen-Galerie, Challenge-Uebersicht, Punkte-Verlauf, AYU Score, Level-Pfad
 */
import { useState, useMemo } from "react";
import { Link } from "wouter";
import { useClientAuth } from "@/contexts/ClientAuthContext";
import { trpc } from "@/lib/trpc";
import {
  ArrowLeft,
  Star,
  Trophy,
  Target,
  Flame,
  Zap,
  TrendingUp,
  Snowflake,
  ChevronRight,
  Lock,
  CheckCircle2,
  Clock,
  Award,
  Loader2,
  Sparkles,
  BookOpen,
  MessageCircle,
  Sun,
  Moon,
  Headphones,
  ChefHat,
  Users,
  Microscope,
  Gem,
  Flower2,
  Brain,
  Sunrise,
  Shield,
  HelpCircle,
  Info,
  Dumbbell,
  Utensils,
  BedDouble,
} from "lucide-react";
import {
  AYU_LEVELS,
  BADGES,
  PRANA_POINTS,
  AYU_SCORE_FACTORS,
  type Badge,
  type BadgeCategory,
} from "@shared/gamificationConfig";
import { useI18n } from "@/contexts/I18nContext";

/* Icon-Mapping fuer Badges */
const BADGE_ICONS: Record<string, React.ReactNode> = {
  seedling: <Sparkles className="w-5 h-5" />,
  "book-open": <BookOpen className="w-5 h-5" />,
  library: <BookOpen className="w-5 h-5" />,
  "message-circle": <MessageCircle className="w-5 h-5" />,
  brain: <Brain className="w-5 h-5" />,
  sunrise: <Sunrise className="w-5 h-5" />,
  flame: <Flame className="w-5 h-5" />,
  users: <Users className="w-5 h-5" />,
  headphones: <Headphones className="w-5 h-5" />,
  "flower-2": <Flower2 className="w-5 h-5" />,
  "chef-hat": <ChefHat className="w-5 h-5" />,
  sun: <Sun className="w-5 h-5" />,
  moon: <Moon className="w-5 h-5" />,
  gem: <Gem className="w-5 h-5" />,
  microscope: <Microscope className="w-5 h-5" />,
  sparkles: <Sparkles className="w-5 h-5" />,
};

const CATEGORY_LABELS: Record<BadgeCategory, string> = {
  tagebuch: "Tagebuch",
  meditation: "Meditation",
  ernaehrung: "Ernaehrung",
  schlaf: "Schlaf",
  routine: "Routine",
  coach: "Coach",
  kurse: "Kurse",
  community: "Community",
  dokumente: "Dokumente",
  allgemein: "Allgemein",
};

export default function AppGamification() {
  const { t } = useI18n();
  const { client, loading: authLoading } = useClientAuth();
  const [activeTab, setActiveTab] = useState<"uebersicht" | "abzeichen" | "challenges" | "verlauf">("uebersicht");

  const summary = trpc.gamification.summary.useQuery(
    { clientId: client?.clientId ?? "" },
    { enabled: !!client?.clientId, staleTime: 60_000 }
  );

  const badgesData = trpc.gamification.badges.useQuery(
    { clientId: client?.clientId ?? "" },
    { enabled: !!client?.clientId && (activeTab === "abzeichen" || activeTab === "uebersicht") }
  );

  const challengeHistory = trpc.gamification.challengeHistory.useQuery(
    { clientId: client?.clientId ?? "" },
    { enabled: !!client?.clientId && activeTab === "challenges" }
  );

  const pointsHistory = trpc.gamification.pointsHistory.useQuery(
    { clientId: client?.clientId ?? "", limit: 30 },
    { enabled: !!client?.clientId && activeTab === "verlauf" }
  );

  const scoreHistory = trpc.gamification.scoreHistory.useQuery(
    { clientId: client?.clientId ?? "", days: 14 },
    { enabled: !!client?.clientId && activeTab === "uebersicht" }
  );

  if (authLoading || !client) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <Loader2 className="w-6 h-6 animate-spin text-[#6b7c3e]" />
      </div>
    );
  }

  const data = summary.data;
  const gesamtStreak = data?.streaks?.find((s: { streakType: string }) => s.streakType === "gesamt");

  return (
    <div className="max-w-2xl mx-auto px-4 py-6 space-y-6">
      {/* Header */}
      <div className="flex items-center gap-3">
        <Link href="/app">
          <button className="p-2 rounded-lg hover:bg-[#f0f3eb] transition-colors">
            <ArrowLeft className="w-5 h-5 text-[#2d3a1e]" />
          </button>
        </Link>
        <div>
          <h1 className="text-xl font-bold text-[#2d3a1e]" style={{ fontFamily: "'Playfair Display', serif" }}>
            {t("gamification.title")}
          </h1>
          <p className="text-xs text-[#8a9680]">{t("gamification.subtitle")}</p>
        </div>
      </div>

      {/* Level-Karte */}
      {data && (
        <div className="rounded-2xl border border-[#d4dcc4] bg-gradient-to-br from-white to-[#f5f3eb] p-6">
          <div className="flex items-center gap-4 mb-4">
            <div
              className="w-14 h-14 rounded-full flex items-center justify-center text-white text-xl font-bold"
              style={{ backgroundColor: data.level.color, fontFamily: "'Playfair Display', serif" }}
            >
              {data.level.id}
            </div>
            <div className="flex-1">
              <h2 className="text-lg font-bold text-[#2d3a1e]" style={{ fontFamily: "'Playfair Display', serif" }}>
                {data.level.name}
              </h2>
              <p className="text-sm text-[#8b7a5e] italic">{data.level.nameSanskrit}</p>
              <p className="text-xs text-[#8a9680] mt-0.5">{data.level.description}</p>
            </div>
          </div>

          {/* Progress */}
          <div className="mb-3">
            <div className="flex justify-between text-xs text-[#8a9680] mb-1">
              <span>{data.totalPoints.toLocaleString("de-DE")} Prana-Punkte</span>
              <span>{data.levelProgress}{t("gamification.zuNaechstemLevel")}</span>
            </div>
            <div className="h-3 bg-[#e8e4d8] rounded-full overflow-hidden">
              <div
                className="h-full rounded-full transition-all duration-1000"
                style={{
                  width: `${data.levelProgress}%`,
                  background: `linear-gradient(90deg, ${data.level.color}88, ${data.level.color})`,
                }}
              />
            </div>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
            <div className="text-center p-2 bg-white/60 rounded-lg">
              <Zap className="w-4 h-4 mx-auto mb-0.5 text-[#8b6914]" />
              <p className="text-sm font-bold text-[#2d3a1e]">{data.totalPoints.toLocaleString("de-DE")}</p>
              <p className="text-[9px] text-[#8a9680]">{t("gamification.prana")}</p>
            </div>
            <div className="text-center p-2 bg-white/60 rounded-lg">
              <Flame className="w-4 h-4 mx-auto mb-0.5" style={{ color: (gesamtStreak?.currentStreak ?? 0) > 0 ? "#E8856C" : "#8a9680" }} />
              <p className="text-sm font-bold text-[#2d3a1e]">{gesamtStreak?.currentStreak ?? 0}</p>
              <p className="text-[9px] text-[#8a9680]">{t("gamification.streak")}</p>
            </div>
            <div className="text-center p-2 bg-white/60 rounded-lg">
              <TrendingUp className="w-4 h-4 mx-auto mb-0.5 text-[#6b7c3e]" />
              <p className="text-sm font-bold text-[#2d3a1e]">{data.ayuScore?.totalScore ?? "—"}</p>
              <p className="text-[9px] text-[#8a9680]">{t("gamification.score")}</p>
            </div>
            <div className="text-center p-2 bg-white/60 rounded-lg">
              <Trophy className="w-4 h-4 mx-auto mb-0.5 text-[#8b6914]" />
              <p className="text-sm font-bold text-[#2d3a1e]">{data.badges.length}</p>
              <p className="text-[9px] text-[#8a9680]">{t("gamification.badges")}</p>
            </div>
          </div>
        </div>
      )}

      {/* Tabs */}
      <div className="flex gap-1 bg-[#f0f3eb] rounded-xl p-1">
        {(["uebersicht", "abzeichen", "challenges", "verlauf"] as const).map((tab) => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            className={`flex-1 py-2 px-3 rounded-lg text-xs font-medium transition-all ${
              activeTab === tab
                ? "bg-white text-[#2d3a1e] shadow-sm"
                : "text-[#8a9680] hover:text-[#6b7c3e]"
            }`}
          >
            {tab === "uebersicht" ? t("gamification.uebersicht") : tab === "abzeichen" ? t("gamification.abzeichen") : tab === "challenges" ? t("gamification.challenges") : t("gamification.verlauf")}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      {activeTab === "uebersicht" && (
        <div className="space-y-4">
          {/* Erklaerung: Was bedeutet das alles? */}
          <div className="rounded-2xl border border-[#d4dcc4] bg-gradient-to-br from-[#f5f3eb] to-white p-5">
            <h3 className="text-sm font-semibold text-[#2d3a1e] mb-3 flex items-center gap-2">
              <Info className="w-4 h-4 text-[#6b7c3e]" />
              {t("gamification.soFunktioniert")}
            </h3>
            <p className="text-xs text-[#5a6b4a] leading-relaxed mb-4">
              Dein AYU-Pfad begleitet dich spielerisch auf deiner Gesundheitsreise. Jede Aktivitaet bringt dir Punkte und Fortschritte.
              Hier erfaehrst du, was die einzelnen Elemente bedeuten und wie du vorankommst.
            </p>

            {/* Prana-Punkte */}
            <div className="mb-4">
              <div className="flex items-center gap-2 mb-2">
                <div className="w-7 h-7 rounded-full bg-[#8b6914]/10 flex items-center justify-center">
                  <Zap className="w-3.5 h-3.5 text-[#8b6914]" />
                </div>
                <span className="text-xs font-semibold text-[#2d3a1e]">Prana-Punkte</span>
              </div>
              <p className="text-[11px] text-[#5a6b4a] leading-relaxed ml-9 mb-2">
                Prana (Lebensenergie) ist deine Hauptwaehrung. Du sammelst Prana-Punkte durch jede Aktivitaet in AYU.
                Je mehr Punkte du sammelst, desto hoeher steigst du im AYU-Pfad auf.
              </p>
              <div className="ml-9 grid grid-cols-1 sm:grid-cols-2 gap-1.5">
                <div className="flex items-center gap-1.5 text-[10px] text-[#8a9680] bg-white/60 rounded-lg px-2 py-1.5">
                  <BookOpen className="w-3 h-3 shrink-0" />
                  <span>{t("gamification.tagebuchEintrag")} <strong className="text-[#2d3a1e]">+{PRANA_POINTS.tagebuch_eintrag}</strong></span>
                </div>
                <div className="flex items-center gap-1.5 text-[10px] text-[#8a9680] bg-white/60 rounded-lg px-2 py-1.5">
                  <MessageCircle className="w-3 h-3 shrink-0" />
                  <span>{t("gamification.coachSitzung")} <strong className="text-[#2d3a1e]">+{PRANA_POINTS.coach_session}</strong></span>
                </div>
                <div className="flex items-center gap-1.5 text-[10px] text-[#8a9680] bg-white/60 rounded-lg px-2 py-1.5">
                  <Sunrise className="w-3 h-3 shrink-0" />
                  <span>{t("gamification.tagesroutineLabel")} <strong className="text-[#2d3a1e]">+{PRANA_POINTS.tagesroutine_abgeschlossen}</strong></span>
                </div>
                <div className="flex items-center gap-1.5 text-[10px] text-[#8a9680] bg-white/60 rounded-lg px-2 py-1.5">
                  <Headphones className="w-3 h-3 shrink-0" />
                  <span>{t("gamification.meditation")} <strong className="text-[#2d3a1e]">+{PRANA_POINTS.meditation_session}</strong></span>
                </div>
                <div className="flex items-center gap-1.5 text-[10px] text-[#8a9680] bg-white/60 rounded-lg px-2 py-1.5">
                  <ChefHat className="w-3 h-3 shrink-0" />
                  <span>{t("gamification.rezeptGekocht")} <strong className="text-[#2d3a1e]">+{PRANA_POINTS.rezept_gekocht}</strong></span>
                </div>
                <div className="flex items-center gap-1.5 text-[10px] text-[#8a9680] bg-white/60 rounded-lg px-2 py-1.5">
                  <Users className="w-3 h-3 shrink-0" />
                  <span>{t("gamification.communityBeitrag")} <strong className="text-[#2d3a1e]">+{PRANA_POINTS.community_beitrag}</strong></span>
                </div>
              </div>
            </div>

            {/* Streak */}
            <div className="mb-4">
              <div className="flex items-center gap-2 mb-2">
                <div className="w-7 h-7 rounded-full bg-[#E8856C]/10 flex items-center justify-center">
                  <Flame className="w-3.5 h-3.5 text-[#E8856C]" />
                </div>
                <span className="text-xs font-semibold text-[#2d3a1e]">Streak (Serie)</span>
              </div>
              <p className="text-[11px] text-[#5a6b4a] leading-relaxed ml-9">
                Dein Streak zaehlt, wie viele Tage in Folge du AYU aktiv genutzt hast (z.B. Tagebuch, Coach, Routine).
                Je laenger deine Serie, desto mehr Bonus-Prana erhaeltst du:
                7 Tage = <strong>+{PRANA_POINTS.streak_7_tage}</strong>, 30 Tage = <strong>+{PRANA_POINTS.streak_30_tage}</strong>, 100 Tage = <strong>+{PRANA_POINTS.streak_100_tage}</strong> Prana.
                Ein verpasster Tag setzt den Streak zurueck, aber du kannst Streak-Freezes nutzen (1x/Monat Basis, 3x/Monat Premium).
              </p>
            </div>

            {/* AYU Score */}
            <div className="mb-4">
              <div className="flex items-center gap-2 mb-2">
                <div className="w-7 h-7 rounded-full bg-[#6b7c3e]/10 flex items-center justify-center">
                  <TrendingUp className="w-3.5 h-3.5 text-[#6b7c3e]" />
                </div>
                <span className="text-xs font-semibold text-[#2d3a1e]">AYU Score (0–100)</span>
              </div>
              <p className="text-[11px] text-[#5a6b4a] leading-relaxed ml-9 mb-2">
                Dein AYU Score zeigt, wie ausgeglichen dein Lebensstil gerade ist. Er berechnet sich taeglich aus mehreren Faktoren:
              </p>
              <div className="ml-9 space-y-1">
                {AYU_SCORE_FACTORS.map((factor) => (
                  <div key={factor.id} className="flex items-center gap-2 text-[10px] text-[#8a9680] bg-white/60 rounded-lg px-2 py-1.5">
                    <span className="font-medium text-[#2d3a1e] min-w-[80px]">{factor.name}</span>
                    <span className="flex-1">{factor.description}</span>
                    {factor.premiumOnly && (
                      <span className="text-[8px] bg-[#8b6914]/10 text-[#8b6914] px-1 py-0.5 rounded shrink-0">Premium</span>
                    )}
                  </div>
                ))}
              </div>
            </div>

            {/* Badges */}
            <div className="mb-4">
              <div className="flex items-center gap-2 mb-2">
                <div className="w-7 h-7 rounded-full bg-[#8b6914]/10 flex items-center justify-center">
                  <Trophy className="w-3.5 h-3.5 text-[#8b6914]" />
                </div>
                <span className="text-xs font-semibold text-[#2d3a1e]">Abzeichen (Badges)</span>
              </div>
              <p className="text-[11px] text-[#5a6b4a] leading-relaxed ml-9">
                Abzeichen sind Meilensteine, die du fuer bestimmte Leistungen erhaeltst — z.B. deinen ersten Tagebuch-Eintrag,
                eine 7-Tage-Serie oder 20 Coach-Sitzungen. Jedes Abzeichen bringt Bonus-Prana.
                Im Basis-Paket gibt es 8 Abzeichen, mit Premium ueber 15.
                Schau im Tab "Abzeichen" nach, welche du schon verdient hast!
              </p>
            </div>

            {/* Level-Pfad */}
            <div className="mb-4">
              <div className="flex items-center gap-2 mb-2">
                <div className="w-7 h-7 rounded-full bg-[#4A7C3F]/10 flex items-center justify-center">
                  <Star className="w-3.5 h-3.5 text-[#4A7C3F]" />
                </div>
                <span className="text-xs font-semibold text-[#2d3a1e]">7 Level — Vom Samen zum Hain</span>
              </div>
              <p className="text-[11px] text-[#5a6b4a] leading-relaxed ml-9">
                Dein AYU-Pfad hat 7 Level, inspiriert vom Wachstum einer Pflanze.
                Du startest als "Samen" (Bija) und kannst bis zum "Hain" (Vana) aufsteigen.
                Die ersten 4 Level sind im Basis-Paket erreichbar, Level 5–7 erfordern Premium.
                Jeder Level-Aufstieg bringt <strong>+{PRANA_POINTS.level_aufstieg} Prana</strong> und schaltet neue Belohnungen frei.
              </p>
            </div>

            {/* Challenges */}
            <div className="mb-4">
              <div className="flex items-center gap-2 mb-2">
                <div className="w-7 h-7 rounded-full bg-[#6b7f4e]/10 flex items-center justify-center">
                  <Target className="w-3.5 h-3.5 text-[#6b7f4e]" />
                </div>
                <span className="text-xs font-semibold text-[#2d3a1e]">Woechentliche Challenges</span>
              </div>
              <p className="text-[11px] text-[#5a6b4a] leading-relaxed ml-9">
                Jede Woche erhaeltst du neue Herausforderungen, die dich motivieren, bestimmte Aktivitaeten zu machen —
                z.B. "Schreibe 3 Tagebuch-Eintraege" oder "Fuehre 2 Coach-Sitzungen".
                Abgeschlossene Challenges bringen <strong>75–200 Prana</strong>.
                Im Basis-Paket gibt es 1 Challenge/Woche, mit Premium bis zu 3.
              </p>
            </div>

            {/* Hilfe-Assistent Verweis */}
            <div className="mt-4 pt-3 border-t border-[#d4dcc4]">
              <Link href="/app/hilfe">
                <button className="flex items-center gap-2 text-xs text-[#6b7c3e] hover:text-[#4A7C3F] transition-colors group">
                  <HelpCircle className="w-4 h-4" />
                  <span>Noch Fragen? Frag den <strong>AYU Hilfe-Assistenten</strong></span>
                  <ChevronRight className="w-3 h-3 group-hover:translate-x-0.5 transition-transform" />
                </button>
              </Link>
            </div>
          </div>

          {/* AYU-Pfad Visualisierung */}
          <div className="rounded-2xl border border-[#d4dcc4] bg-white p-5">
            <h3 className="text-sm font-semibold text-[#2d3a1e] mb-4 flex items-center gap-2">
              <Star className="w-4 h-4 text-[#8b6914]" />
              {t("gamification.ayuPfad7Level")}
            </h3>
            <div className="space-y-3">
              {AYU_LEVELS.map((lvl) => {
                const isCurrentOrPast = data && data.level.id >= lvl.id;
                const isCurrent = data && data.level.id === lvl.id;
                const isLocked = lvl.premiumOnly && client.packageType !== "premium";
                return (
                  <div
                    key={lvl.id}
                    className={`flex items-center gap-3 p-3 rounded-xl transition-all ${
                      isCurrent
                        ? "bg-gradient-to-r from-[#f5f3eb] to-white border border-[#d4dcc4] shadow-sm"
                        : isCurrentOrPast
                        ? "bg-[#f8f7f2]"
                        : "opacity-50"
                    }`}
                  >
                    <div
                      className={`w-9 h-9 rounded-full flex items-center justify-center text-sm font-bold ${
                        isCurrentOrPast ? "text-white" : "text-[#8a9680] bg-[#e8e4d8]"
                      }`}
                      style={isCurrentOrPast ? { backgroundColor: lvl.color } : {}}
                    >
                      {isLocked ? <Lock className="w-4 h-4" /> : lvl.id}
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <span className={`text-sm font-medium ${isCurrentOrPast ? "text-[#2d3a1e]" : "text-[#8a9680]"}`}>
                          {lvl.name}
                        </span>
                        <span className="text-[10px] text-[#8b7a5e] italic">{lvl.nameSanskrit}</span>
                        {isCurrent && (
                          <span className="text-[9px] bg-[#6b7c3e] text-white px-1.5 py-0.5 rounded-full">{t("gamification.aktuell")}</span>
                        )}
                        {isLocked && (
                          <span className="text-[9px] bg-[#8b6914]/10 text-[#8b6914] px-1.5 py-0.5 rounded-full">Premium</span>
                        )}
                      </div>
                      <p className="text-[10px] text-[#8a9680]">{lvl.requiredPoints.toLocaleString("de-DE")} Prana</p>
                    </div>
                    {isCurrentOrPast && !isCurrent && <CheckCircle2 className="w-4 h-4 text-[#6b7c3e]" />}
                  </div>
                );
              })}
            </div>
          </div>

          {/* AYU Score Chart (simplified) */}
          {scoreHistory.data && scoreHistory.data.length > 0 && (
            <div className="rounded-2xl border border-[#d4dcc4] bg-white p-5">
              <h3 className="text-sm font-semibold text-[#2d3a1e] mb-3 flex items-center gap-2">
                <TrendingUp className="w-4 h-4 text-[#6b7c3e]" />
                {t("gamification.ayuScoreVerlauf")}
              </h3>
              <div className="flex items-end gap-1 h-24">
                {scoreHistory.data.map((score: { id: number; date: string; totalScore: number }, i: number) => (
                  <div key={score.id} className="flex-1 flex flex-col items-center">
                    <div
                      className="w-full rounded-t-sm transition-all"
                      style={{
                        height: `${score.totalScore}%`,
                        backgroundColor: score.totalScore >= 70 ? "#6b7c3e" : score.totalScore >= 40 ? "#8b6914" : "#E8856C",
                        opacity: 0.7 + (i / scoreHistory.data!.length) * 0.3,
                      }}
                    />
                    <span className="text-[8px] text-[#8a9680] mt-1">{score.date.slice(8)}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {activeTab === "abzeichen" && (
        <div className="space-y-4">
          {badgesData.isLoading ? (
            <div className="flex justify-center py-8">
              <Loader2 className="w-5 h-5 animate-spin text-[#6b7c3e]" />
            </div>
          ) : (
            <>
              {/* Verdiente Abzeichen */}
              {badgesData.data?.earned && badgesData.data.earned.length > 0 && (
                <div className="rounded-2xl border border-[#d4dcc4] bg-white p-5">
                  <h3 className="text-sm font-semibold text-[#2d3a1e] mb-3 flex items-center gap-2">
                    <Trophy className="w-4 h-4 text-[#8b6914]" />
                    {t("gamification.verdienteAbzeichen", { n: badgesData.data.earned.length } as any)}
                  </h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {badgesData.data.earned.map((item: { badgeId: string; earnedAt: Date; badge?: Badge }) => (
                      <div key={item.badgeId} className="p-3 rounded-xl bg-gradient-to-br from-[#f5f3eb] to-white border border-[#d4dcc4]">
                        <div className="flex items-center gap-2 mb-1.5">
                          <div className="w-8 h-8 rounded-full bg-[#8b6914]/10 flex items-center justify-center text-[#8b6914]">
                            {item.badge ? (BADGE_ICONS[item.badge.icon] || <Award className="w-5 h-5" />) : <Award className="w-5 h-5" />}
                          </div>
                          <div>
                            <p className="text-xs font-semibold text-[#2d3a1e]">{item.badge?.name || item.badgeId}</p>
                            <p className="text-[9px] text-[#8b7a5e] italic">{item.badge?.nameSanskrit}</p>
                          </div>
                        </div>
                        <p className="text-[10px] text-[#8a9680]">{item.badge?.description}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Alle verfuegbaren Abzeichen */}
              <div className="rounded-2xl border border-[#d4dcc4] bg-white p-5">
                <h3 className="text-sm font-semibold text-[#2d3a1e] mb-3 flex items-center gap-2">
                  <Award className="w-4 h-4 text-[#6b7c3e]" />
                  {t("gamification.alleAbzeichen")}
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  {badgesData.data?.available?.map((badge: Badge & { earned: boolean }) => (
                    <div
                      key={badge.id}
                      className={`p-3 rounded-xl border transition-all ${
                        badge.earned
                          ? "border-[#6b7c3e]/30 bg-[#f0f3eb]"
                          : "border-[#e8e4d8] opacity-60"
                      }`}
                    >
                      <div className="flex items-center gap-2 mb-1">
                        <div className={`w-7 h-7 rounded-full flex items-center justify-center ${
                          badge.earned ? "bg-[#6b7c3e]/10 text-[#6b7c3e]" : "bg-[#e8e4d8] text-[#8a9680]"
                        }`}>
                          {badge.earned ? (BADGE_ICONS[badge.icon] || <Award className="w-4 h-4" />) : <Lock className="w-3.5 h-3.5" />}
                        </div>
                        <div>
                          <p className="text-xs font-medium text-[#2d3a1e]">{badge.name}</p>
                          {badge.premiumOnly && (
                            <span className="text-[8px] bg-[#8b6914]/10 text-[#8b6914] px-1 py-0.5 rounded">Premium</span>
                          )}
                        </div>
                      </div>
                      <p className="text-[10px] text-[#8a9680]">{badge.description}</p>
                      {badge.earned && (
                        <div className="flex items-center gap-1 mt-1">
                          <CheckCircle2 className="w-3 h-3 text-[#6b7c3e]" />
                          <span className="text-[9px] text-[#6b7c3e]">{t("gamification.verdient")}</span>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </>
          )}
        </div>
      )}

      {activeTab === "challenges" && (
        <div className="space-y-4">
          {/* Aktive Challenges */}
          {data?.activeChallenges && data.activeChallenges.length > 0 && (
            <div className="rounded-2xl border border-[#d4dcc4] bg-white p-5">
              <h3 className="text-sm font-semibold text-[#2d3a1e] mb-3 flex items-center gap-2">
                <Target className="w-4 h-4 text-[#6b7f4e]" />
                {t("gamification.aktivesChallenges")}
              </h3>
              <div className="space-y-3">
                {data.activeChallenges.map((ch: {
                  id: number; title: string; description: string | null;
                  currentCount: number; targetCount: number; rewardPoints: number; status: string;
                }) => {
                  const progress = Math.round((ch.currentCount / ch.targetCount) * 100);
                  return (
                    <div key={ch.id} className="p-4 rounded-xl border border-[#d4dcc4] bg-[#f8f7f2]">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-sm font-medium text-[#2d3a1e]">{ch.title}</span>
                        <span className="text-xs text-[#8b6914] flex items-center gap-0.5">
                          <Zap className="w-3 h-3" /> +{ch.rewardPoints}
                        </span>
                      </div>
                      {ch.description && <p className="text-xs text-[#8a9680] mb-2">{ch.description}</p>}
                      <div className="h-2 bg-[#e8e4d8] rounded-full overflow-hidden mb-1">
                        <div
                          className="h-full rounded-full transition-all duration-500"
                          style={{ width: `${progress}%`, backgroundColor: "#6b7c3e" }}
                        />
                      </div>
                      <p className="text-[10px] text-[#8a9680]">{ch.currentCount}/{ch.targetCount} {t("gamification.erledigt")}</p>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Challenge-Verlauf */}
          {challengeHistory.data && challengeHistory.data.length > 0 && (
            <div className="rounded-2xl border border-[#d4dcc4] bg-white p-5">
              <h3 className="text-sm font-semibold text-[#2d3a1e] mb-3 flex items-center gap-2">
                <Clock className="w-4 h-4 text-[#8a9680]" />
                {t("gamification.vergangenesChallenges")}
              </h3>
              <div className="space-y-2">
                {challengeHistory.data.map((ch: {
                  id: number; title: string; status: string;
                  currentCount: number; targetCount: number; rewardPoints: number; weekStart: string;
                }) => (
                  <div key={ch.id} className="flex items-center gap-3 p-2 rounded-lg hover:bg-[#f8f7f2]">
                    {ch.status === "completed" ? (
                      <CheckCircle2 className="w-4 h-4 text-[#6b7c3e] shrink-0" />
                    ) : (
                      <Clock className="w-4 h-4 text-[#8a9680] shrink-0" />
                    )}
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-medium text-[#2d3a1e] truncate">{ch.title}</p>
                      <p className="text-[10px] text-[#8a9680]">KW {ch.weekStart}</p>
                    </div>
                    <span className={`text-[10px] px-1.5 py-0.5 rounded ${
                      ch.status === "completed" ? "bg-[#6b7c3e]/10 text-[#6b7c3e]" : "bg-[#8a9680]/10 text-[#8a9680]"
                    }`}>
                      {ch.status === "completed" ? t("gamification.erledigtLabel") : t("gamification.abgelaufen")}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {activeTab === "verlauf" && (
        <div className="space-y-4">
          {pointsHistory.isLoading ? (
            <div className="flex justify-center py-8">
              <Loader2 className="w-5 h-5 animate-spin text-[#6b7c3e]" />
            </div>
          ) : pointsHistory.data && pointsHistory.data.length > 0 ? (
            <div className="rounded-2xl border border-[#d4dcc4] bg-white p-5">
              <h3 className="text-sm font-semibold text-[#2d3a1e] mb-3 flex items-center gap-2">
                <Zap className="w-4 h-4 text-[#8b6914]" />
                {t("gamification.pranaVerlauf")}
              </h3>
              <div className="space-y-2">
                {pointsHistory.data.map((entry: {
                  id: number; activityType: string; points: number;
                  description: string | null; createdAt: Date;
                }) => (
                  <div key={entry.id} className="flex items-center gap-3 p-2 rounded-lg hover:bg-[#f8f7f2]">
                    <div className="w-8 h-8 rounded-full bg-[#8b6914]/10 flex items-center justify-center">
                      <Zap className="w-3.5 h-3.5 text-[#8b6914]" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-medium text-[#2d3a1e] truncate">
                        {entry.description || entry.activityType.replace(/_/g, " ")}
                      </p>
                      <p className="text-[10px] text-[#8a9680]">
                        {new Date(entry.createdAt).toLocaleDateString("de-DE", {
                          day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit"
                        })}
                      </p>
                    </div>
                    <span className="text-sm font-semibold text-[#6b7c3e]">+{entry.points}</span>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <div className="rounded-2xl border border-[#d4dcc4] bg-white p-8 text-center">
              <Zap className="w-8 h-8 mx-auto text-[#8a9680] mb-2" />
              <p className="text-sm text-[#8a9680]">{t("gamification.nochKeinePunkte")}</p>
              <p className="text-xs text-[#8a9680] mt-1">{t("gamification.nochKeinePunkteDesc")}</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
